import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import _ from 'lodash';
import { CostCenter } from '../../models/cost-center/cost-center';
import { CostCenterProxyService } from '../../proxies/cost-center.proxy.service';
import { LocaleHelperService } from '../../services/locale-helper.service';

/** The filter panel's controls; the status select arrives as a string ('true' / 'false'). */
interface CostCenterFilterFormModel {
  search: string | null;
  isActive: string | null;
}

/** What the panel emits on apply — a persisted filter set may omit either field. */
type CostCenterFilters = Partial<CostCenterFilterFormModel>;

interface CostCenterRow {
  code: string;
  name: string;
  level: number | null;
  selectable: boolean;
  isActive: boolean;
}

/**
 * Cost centers (#5841) — read-only tree of the cost-center dimension GL account rules key on. A rule on a
 * parent is inherited by every child below it, which is why the hierarchy is the point of this view.
 * Loaded once (GET cost-center/all) and filtered client-side so a match keeps its ancestors visible.
 */
@Component({
  selector: 'app-cost-centers',
  templateUrl: './cost-centers.component.html',
  styleUrl: './cost-centers.component.scss',
})
export class CostCentersComponent implements OnInit {
  private readonly proxy = inject(CostCenterProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translateService = inject(TranslateService);

  readonly isLoading = signal(0);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'cost-center-filters', config: this.filterConfig });
  readonly costCenters = signal<CostCenter[]>([]);
  readonly searchTerm = signal('');
  /** Null = every status. */
  readonly activeFilter = signal<boolean | null>(null);

  private readonly byCode = computed(() => new Map(this.costCenters().map((c) => [c.code, c])));

  private readonly matchingCodes = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const activeFilter = this.activeFilter();
    const codes = new Set<string>();
    for (const center of this.costCenters()) {
      // Older nodes carry no flag at all; only an explicit false means inactive.
      if (!_.isNil(activeFilter) && (center.isActive !== false) !== activeFilter) continue;
      if (term && !center.code.toLowerCase().includes(term) && !this.localized(center).toLowerCase().includes(term)) continue;
      codes.add(center.code);
    }
    return codes;
  });

  /** Matches plus all their ancestors, so a hit deep in the tree is still reachable from its root. */
  private readonly includedCodes = computed(() => {
    const byCode = this.byCode();
    const include = new Set<string>();
    for (const code of this.matchingCodes()) {
      let current = byCode.get(code);
      while (!_.isNil(current) && !include.has(current.code)) {
        include.add(current.code);
        current = _.isNil(current.parentCode) ? undefined : byCode.get(current.parentCode);
      }
    }
    return include;
  });

  readonly matchCount = computed(() => this.matchingCodes().size);

  readonly treeNodes = computed<TreeNode<CostCenterRow>[]>(() => {
    const include = this.includedCodes();
    const visible = this.costCenters().filter((c) => include.has(c.code));
    return this.buildTree(visible, this.searchTerm().trim().length > 0);
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as CostCenterFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.load();
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('CostCenter_Filter_Search', 'Code or name'),
          placeholder: this.t('CostCenter_Search', 'Search by code or name'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'isActive',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: [
            { label: this.t('CostCenter_Active', 'Active'), value: 'true' },
            { label: this.t('CostCenter_Inactive', 'Inactive'), value: 'false' },
          ],
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: CostCenterFilters): void {
    this.searchTerm.set((filters.search ?? '').toString().trim());
    this.activeFilter.set(_.isNil(filters.isActive) || filters.isActive === '' ? null : filters.isActive === 'true');
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private load(): void {
    this.startLoading();
    this.proxy
      .getAll()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.costCenters.set(response.data ?? []),
        error: () => this.costCenters.set([]),
      });
  }

  /**
   * Roots are nodes with no parent, or whose parent is filtered out or missing from the data. While
   * searching every node is expanded so the matches are visible; otherwise only the roots are.
   */
  private buildTree(centers: CostCenter[], expandAll: boolean): TreeNode<CostCenterRow>[] {
    const codes = new Set(centers.map((c) => c.code));
    const childrenByParent = new Map<string, CostCenter[]>();
    for (const center of centers) {
      const parent = !_.isNil(center.parentCode) && codes.has(center.parentCode) ? center.parentCode : '';
      const siblings = childrenByParent.get(parent) ?? [];
      siblings.push(center);
      childrenByParent.set(parent, siblings);
    }

    const byCode = (a: CostCenter, b: CostCenter) => a.code.localeCompare(b.code);
    const make = (center: CostCenter, isRoot: boolean): TreeNode<CostCenterRow> => {
      const kids = (childrenByParent.get(center.code) ?? []).slice().sort(byCode);
      return {
        key: center.code,
        data: this.toRow(center),
        children: kids.map((kid) => make(kid, false)),
        expanded: kids.length > 0 && (expandAll || isRoot),
        leaf: kids.length === 0,
      };
    };
    return (childrenByParent.get('') ?? [])
      .slice()
      .sort(byCode)
      .map((root) => make(root, true));
  }

  private toRow(center: CostCenter): CostCenterRow {
    return {
      code: center.code,
      name: this.localized(center),
      level: center.level,
      selectable: center.selectable === true,
      isActive: center.isActive !== false,
    };
  }

  private localized(center: CostCenter): string {
    return this.locale.getLocalizedValue(center.name) || center.code;
  }
}
